import React, { useRef, useState, useMemo, useEffect, useCallback } from "react";
import ReactGantt, { Task, Link, Marker, GanttConfig, ReactGanttRef, GanttTemplates, ReactGanttProps } from "@dhx/react-gantt";
import "@dhx/react-gantt/dist/react-gantt.css";
import TaskEditor from "./TaskEditor";
import ContextMenu from "./ContextMenu";
import ConfirmDialog from "./ConfirmDialog";
import PromptDialog from "./PromptDialog";
import PlanPicker from "./PlanPicker";
import PlanSettings from "./PlanSettings";
import ShareDialog from "./ShareDialog";
import RatingDialog from "./RatingDialog";
import * as planService from "../services/planService";
import { PlanMeta, FullPlan } from "../services/planService";
import { getPlatformContext } from "../services/platformContext";
import "./Tab.css";

interface ColumnVisibility {
  text: boolean;
  assigned_to: boolean;
  start_date: boolean;
  duration: boolean;
  status: boolean;
}

const STATUS_OPTIONS = [
  { value: "", label: "None", color: "transparent" },
  { value: "in_progress", label: "WIP", color: "#1976D2" },
  { value: "on_hold", label: "HOLD", color: "#E65100" },
  { value: "at_risk", label: "RISK", color: "#C62828" },
  { value: "delayed", label: "DELAY", color: "#6A1B9A" },
  { value: "done", label: "DONE", color: "#2E7D32" },
  { value: "cancelled", label: "CANCEL", color: "#555" },
];

interface ContextMenuState {
  x: number;
  y: number;
  items: { label: string; onClick: () => void; danger?: boolean }[];
}

interface MarkerData {
  id: string;
  text: string;
  start_date: Date;
  color: string;
}

let ganttTemplates: GanttTemplates = {};

// Download a file from URL as a blob (works inside Teams where navigation is blocked)
async function downloadFromUrl(url: string, filename: string) {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(blobUrl);
  } catch {
    // Fallback: open in new tab
    window.open(url, "_blank");
  }
}
let markerIdCounter = 1;

// Serialize a Date to a string for storage (local time, no UTC shift)
function serializeDate(d: Date | string | null | undefined): string | null {
  if (!d) return null;
  // If it's already a string, normalize it through parseDate to ensure consistent format
  const date = typeof d === "string" ? parseDate(d) : d;
  if (!date || isNaN(date.getTime())) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const h = String(date.getHours()).padStart(2, "0");
  const min = String(date.getMinutes()).padStart(2, "0");
  return `${y}-${m}-${day} ${h}:${min}`;
}

// Parse a stored date string back to a Date (local time)
function parseDate(s: string | null | undefined): Date | null {
  if (!s) return null;
  if (typeof s !== "string") return isNaN((s as any).getTime()) ? null : s as any;

  // "YYYY-MM-DD HH:MM" or "YYYY-MM-DDTHH:MM"
  const isoMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2}))?/);
  if (isoMatch) {
    const d = new Date(
      Number(isoMatch[1]), Number(isoMatch[2]) - 1, Number(isoMatch[3]),
      Number(isoMatch[4] || 0), Number(isoMatch[5] || 0)
    );
    if (!isNaN(d.getTime())) return d;
  }

  // "DD-MM-YYYY HH:MM" (gantt default format)
  const ganttMatch = s.match(/^(\d{2})-(\d{2})-(\d{4})(?:\s+(\d{2}):(\d{2}))?/);
  if (ganttMatch) {
    const d = new Date(
      Number(ganttMatch[3]), Number(ganttMatch[2]) - 1, Number(ganttMatch[1]),
      Number(ganttMatch[4] || 0), Number(ganttMatch[5] || 0)
    );
    if (!isNaN(d.getTime())) return d;
  }

  // Last resort
  const d = new Date(s);
  if (!isNaN(d.getTime())) return d;
  return null;
}

// Convert API task to gantt task format
function apiTaskToGantt(t: planService.PlanTask): Task | null {
  const startDate = parseDate(t.start_date);
  if (!startDate) return null; // skip invalid tasks

  const result: any = {
    id: t.id,
    text: t.text,
    type: t.taskType,
    parent: t.parentId || 0,
    color: t.color || undefined,
    status: t.status,
    assigned_to: t.assigned_to,
    start_date: startDate,
    progress: t.progress || 0,
    open: t.open !== false,
    order: t.order,
  };

  const endDate = parseDate(t.end_date);
  if (endDate) {
    result.end_date = endDate;
  } else if (t.duration != null) {
    result.duration = t.duration;
  } else {
    result.duration = 1;
  }

  return result;
}

function apiLinkToGantt(l: planService.PlanLink): Link {
  return { id: l.id, source: l.source, target: l.target, type: l.linkType };
}

function apiMarkerToLocal(m: planService.PlanMarker): MarkerData | null {
  const d = parseDate(m.start_date);
  if (!d) return null;
  return { id: m.id, text: m.text, start_date: d, color: m.color };
}

function Tab({ inTeams, initialDarkMode }: { inTeams?: boolean; initialDarkMode?: boolean }) {
  const ganttRef = useRef<ReactGanttRef>(null);

  // Plan state
  const [plans, setPlans] = useState<PlanMeta[]>([]);
  const [activePlanId, setActivePlanId] = useState<string | null>(null);
  const [planData, setPlanData] = useState<{ tasks: Task[]; links: Link[] }>({ tasks: [], links: [] });
  const [markersState, setMarkersState] = useState<MarkerData[]>([]);
  const [lastModified, setLastModified] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [canEdit, setCanEdit] = useState(true);

  // UI state
  const [columnVisibility, setColumnVisibility] = useState<ColumnVisibility>(() => {
    const isMobile = window.innerWidth <= 768;
    const defaults: ColumnVisibility = { text: !isMobile, assigned_to: false, start_date: false, duration: false, status: false };
    try {
      const saved = localStorage.getItem("gantt-column-visibility");
      if (saved) {
        const parsed = JSON.parse(saved);
        // Merge with defaults so new keys get their default value
        return { ...defaults, ...parsed };
      }
    } catch {}
    return defaults;
  });

  useEffect(() => {
    localStorage.setItem("gantt-column-visibility", JSON.stringify(columnVisibility));
  }, [columnVisibility]);

  const [menuOpen, setMenuOpen] = useState(false);
  const [zoomMenuOpen, setZoomMenuOpen] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const [zoomLevel, setZoomLevel] = useState("day");
  const [viewStart, setViewStart] = useState("");
  const [viewEnd, setViewEnd] = useState("");
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem("gantt-dark-mode");
    if (saved !== null) return saved === "true";
    return initialDarkMode || false;
  });
  const [confirmDialog, setConfirmDialog] = useState<{ message: string; onConfirm: () => void } | null>(null);
  const [editingMarker, setEditingMarker] = useState<MarkerData | null>(null);
  const [showPlanSettings, setShowPlanSettings] = useState(false);
  const [showCreatePrompt, setShowCreatePrompt] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState<"share" | "join" | null>(null);
  const [showRating, setShowRating] = useState(false);
  const [ratingChecked, setRatingChecked] = useState(false);
  const [ratingPrompted, setRatingPrompted] = useState(false);
  const taskActionCount = useRef(0);

  const closeContextMenu = useCallback(() => setContextMenu(null), []);
  const [contextId, setContextId] = useState<string>("");

  // ============================================
  // Platform init and plan loading
  // ============================================

  useEffect(() => {
    async function init() {
      try {
        const platform = getPlatformContext(inTeams);
        await platform.initialize();

        const ctxId = platform.getContextId() || "local-default";
        setContextId(ctxId);

        // Store auth token for API calls
        const token = await platform.getAuthToken();
        if (token) {
          localStorage.setItem("gantt-auth-token", token);
          // Track login
          fetch(`${process.env.REACT_APP_FUNC_ENDPOINT || ""}/api/track-login`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({ platform: inTeams ? "teams" : "web" }),
          }).catch(() => {});
          // Check if user has already been prompted for rating (Teams only)
          if (inTeams) {
            fetch(`${process.env.REACT_APP_FUNC_ENDPOINT || ""}/api/rating/status`, {
              headers: { Authorization: `Bearer ${token}` },
            }).then(r => r.json()).then(data => {
              setRatingPrompted(!!data.prompted);
              setRatingChecked(true);
            }).catch(() => setRatingChecked(true));
          }
        }

        // Check pending shares for this user
        const userInfo = await platform.getUserInfo();
        if (userInfo?.email) {
          await planService.checkPendingShares(userInfo.email, ctxId).catch(() => {});
        }

        const loadedPlans = await planService.listPlans(ctxId);
        setPlans(loadedPlans);

        const lastUsed = localStorage.getItem("gantt-last-plan-id");
        const planToLoad = loadedPlans.find(p => p.id === lastUsed) || loadedPlans[0];
        if (planToLoad) {
          setActivePlanId(planToLoad.id);
        }
      } catch (err) {
        console.error("Failed to initialize:", err);
        // Fallback to local context
        setContextId("local-default");
      } finally {
        setLoading(false);
      }
    }
    init();
  }, []);

  // Load full plan data when active plan changes
  useEffect(() => {
    if (!activePlanId) return;
    localStorage.setItem("gantt-last-plan-id", activePlanId);

    async function loadPlan() {
      try {
        const full = await planService.getFullPlan(activePlanId!);
        applyPlanData(full);
      } catch (err) {
        console.error("Failed to load plan:", err);
      }
    }
    loadPlan();
  }, [activePlanId]);

  function applyPlanData(full: FullPlan) {
    const tasks = full.tasks.map(apiTaskToGantt).filter(Boolean) as Task[];
    const links = full.links.map(apiLinkToGantt);
    const markers = full.markers.map(apiMarkerToLocal).filter(Boolean) as MarkerData[];
    setPlanData({ tasks, links });
    setMarkersState(markers);
    setCanEdit(full.canEdit !== false);
    setLastModified(full.plan.updatedAt);

    // Set view dates from task range
    if (tasks.length > 0) {
      let minDate: Date | null = null;
      let maxDate: Date | null = null;
      for (const t of tasks) {
        const s = (t as any).start_date as Date;
        const e = (t as any).end_date as Date;
        if (s && (!minDate || s < minDate)) minDate = s;
        if (e && (!maxDate || e > maxDate)) maxDate = e;
        if (s && !e && (!maxDate || s > maxDate)) maxDate = s;
      }
      if (minDate) {
        const padded = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate() - 7);
        setViewStart(dateToStr(padded));
      }
      if (maxDate) {
        const padded = new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate() + 14);
        setViewEnd(dateToStr(padded));
      }
    }
  }

  function dateToStr(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }


  // Sync polling — every 30s
  useEffect(() => {
    if (!activePlanId || !lastModified) return;

    const interval = setInterval(async () => {
      try {
        const updated = await planService.syncPlan(activePlanId, lastModified);
        if (updated) {
          applyPlanData(updated);
        }
      } catch (err) {
        console.error("Sync failed:", err);
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [activePlanId, lastModified]);

  // Plan picker handlers
  async function handleCreatePlan(title: string) {
    try {
      const plan = await planService.createPlan({ title, contextId });
      setPlans(prev => [...prev, plan]);
      setActivePlanId(plan.id);
    } catch (err) {
      console.error("Failed to create plan:", err);
    }
  }

  async function handleRenamePlan(planId: string, title: string) {
    try {
      await planService.updatePlan(planId, { title } as any);
      setPlans(prev => prev.map(p => p.id === planId ? { ...p, title } : p));
    } catch (err) {
      console.error("Failed to rename plan:", err);
    }
  }

  async function handleDeletePlan(planId: string) {
    setConfirmDialog({
      message: `Delete this plan and all its data?`,
      onConfirm: async () => {
        try {
          await planService.deletePlan(planId);
          setPlans(prev => prev.filter(p => p.id !== planId));
          if (activePlanId === planId) {
            const remaining = plans.filter(p => p.id !== planId);
            setActivePlanId(remaining.length > 0 ? remaining[0].id : null);
            if (remaining.length === 0) {
              setPlanData({ tasks: [], links: [] });
              setMarkersState([]);
            }
          }
        } catch (err) {
          console.error("Failed to delete plan:", err);
        }
        setConfirmDialog(null);
      },
    });
  }

  // ============================================
  // Data save callback — auto-saves to API
  // ============================================

  const activePlanIdForSave = useRef(activePlanId);
  activePlanIdForSave.current = activePlanId;

  const ratingPromptedRef = useRef(ratingPrompted);
  ratingPromptedRef.current = ratingPrompted;
  const ratingCheckedRef = useRef(ratingChecked);
  ratingCheckedRef.current = ratingChecked;

  function maybeShowRating() {
    if (!inTeams || !ratingCheckedRef.current || ratingPromptedRef.current) return;
    taskActionCount.current++;
    if (taskActionCount.current < 1) return;
    const gantt = ganttRef.current?.instance;
    if (!gantt || gantt.getTaskCount() < 3) return;

    // Check dismiss history
    const dismissCount = parseInt(localStorage.getItem("gantt-rating-dismiss-count") || "0");
    if (dismissCount >= 2) {
      setRatingPrompted(true);
      return;
    }
    const lastDismiss = localStorage.getItem("gantt-rating-last-dismiss");
    if (lastDismiss) {
      const daysSince = (Date.now() - parseInt(lastDismiss)) / (1000 * 60 * 60 * 24);
      if (daysSince < 3) return;
    }

    setShowRating(true);
    setRatingPrompted(true); // prevent showing again this session
  }

  const dataCallback: ReactGanttProps["data"] = useMemo(() => ({
    save: (entity: string, action: string, data: any, id: string | number) => {
      const planId = activePlanIdForSave.current;
      if (!planId) return;

      if (entity === "task" && (action === "create" || action === "update")) {
        maybeShowRating();
      }

      if (entity === "task") {
        if (action === "create") {
          planService.createTask(planId, {
            text: data.text,
            taskType: data.type,
            parentId: data.parent && data.parent !== 0 ? String(data.parent) : null,
            color: data.color || null,
            status: data.status || "",
            assigned_to: data.assigned_to || "",
            start_date: serializeDate(data.start_date),
            end_date: serializeDate(data.end_date),
            duration: data.duration,
            progress: data.progress || 0,
            order: data.$index || 0,
            open: data.open !== false,
          } as any).then((created) => {
            // Update the gantt's internal ID to match the server ID
            const gantt = ganttRef.current?.instance;
            if (gantt && gantt.isTaskExists(id)) {
              gantt.changeTaskId(id, created.id);
            }
          }).catch(console.error);
        } else if (action === "update") {
          planService.updateTask(planId, String(id), {
            text: data.text,
            taskType: data.type,
            parentId: data.parent && data.parent !== 0 ? String(data.parent) : null,
            color: data.color || null,
            status: data.status || "",
            assigned_to: data.assigned_to || "",
            start_date: serializeDate(data.start_date),
            end_date: serializeDate(data.end_date),
            duration: data.duration,
            progress: data.progress || 0,
            order: data.$index || 0,
            open: data.open !== false,
          } as any).catch(console.error);
        } else if (action === "delete") {
          planService.deleteTask(planId, String(id)).catch(console.error);
        }
      } else if (entity === "link") {
        if (action === "create") {
          planService.createLink(planId, {
            source: String(data.source),
            target: String(data.target),
            linkType: data.type || "0",
          }).then((created) => {
            const gantt = ganttRef.current?.instance;
            if (gantt && gantt.isLinkExists(id)) {
              gantt.changeLinkId(id, created.id);
            }
          }).catch(console.error);
        } else if (action === "delete") {
          planService.deleteLink(planId, String(id)).catch(console.error);
        }
      }
    },
  }), []); // uses ref, no deps needed

  // ============================================
  // Markers (managed separately from gantt data callback)
  // ============================================

  const markers: Marker[] = useMemo(() =>
    markersState.map((m) => ({
      id: m.id,
      start_date: m.start_date,
      text: m.text,
      css: `gantt-marker-${m.id}`,
    })),
    [markersState]
  );

  // Apply dark/light theme
  useEffect(() => {
    document.documentElement.setAttribute("data-gantt-theme", darkMode ? "dark" : "terrace");
    document.documentElement.setAttribute("data-theme", darkMode ? "dark" : "light");
    document.documentElement.style.backgroundColor = darkMode ? "#141414" : "";
    localStorage.setItem("gantt-dark-mode", String(darkMode));
    const gantt = ganttRef.current?.instance;
    if (gantt) {
      gantt.setSkin(darkMode ? "dark" : "terrace");
      gantt.render();
    }
    // Remove preload dark style once skin is applied
    const preload = document.getElementById("gantt-preload-dark");
    if (preload) preload.remove();
  }, [darkMode]);

  // Inject marker color styles
  useEffect(() => {
    let style = document.getElementById("gantt-marker-styles");
    if (!style) {
      style = document.createElement("style");
      style.id = "gantt-marker-styles";
      document.head.appendChild(style);
    }
    style.textContent = markersState.map((m) =>
      `.gantt-marker-${m.id} { border-left-color: ${m.color}; }\n` +
      `.gantt-marker-${m.id} .gantt_marker_content { background: ${m.color}; }`
    ).join("\n");
  }, [markersState]);

  const darkModeRef = useRef(darkMode);
  darkModeRef.current = darkMode;

  const canEditRef = useRef(canEdit);
  canEditRef.current = canEdit;

  const markersRef = useRef(markersState);
  markersRef.current = markersState;

  // Marker context menu
  useEffect(() => {
    function handleMarkerContextMenu(e: MouseEvent) {
      const target = e.target as HTMLElement;
      const markerEl = target.closest("[class*='gantt-marker-']");
      if (!markerEl) return;

      e.preventDefault();
      e.stopPropagation();

      const markerClasses = Array.from(markerEl.classList);
      const markerClass = markerClasses.find(c => c.startsWith("gantt-marker-"));
      if (!markerClass) return;
      const markerId = markerClass.replace("gantt-marker-", "");

      setContextMenu({
        x: e.clientX,
        y: e.clientY,
        items: [
          {
            label: "Edit marker",
            onClick: () => {
              const marker = markersRef.current.find(m => m.id === markerId);
              if (marker) setEditingMarker(marker);
            },
          },
          {
            label: "Delete marker",
            danger: true,
            onClick: () => {
              setMarkersState(prev => prev.filter(m => m.id !== markerId));
              if (activePlanId) {
                planService.deleteMarker(activePlanId, markerId).catch(console.error);
              }
            },
          },
        ],
      });
    }

    document.addEventListener("contextmenu", handleMarkerContextMenu, true);
    return () => document.removeEventListener("contextmenu", handleMarkerContextMenu, true);
  }, [activePlanId]);

  // Gantt init — zoom, templates, context menu
  useEffect(() => {
    const interval = setInterval(() => {
      const gantt = ganttRef.current?.instance;
      if (!gantt) return;

      // Zoom levels
      gantt.ext.zoom.init({
        levels: [
          { name: "day", scale_height: 50, min_column_width: 40, scales: [
            { unit: "month", step: 1, format: "%F %Y" },
            { unit: "day", step: 1, format: "%d" },
          ]},
          { name: "week", scale_height: 50, min_column_width: 50, scales: [
            { unit: "month", step: 1, format: "%F %Y" },
            { unit: "week", step: 1, format: "%d %M" },
          ]},
          { name: "month", scale_height: 50, min_column_width: 50, scales: [
            { unit: "year", step: 1, format: "%Y" },
            { unit: "month", step: 1, format: "%M" },
          ]},
          { name: "year", scale_height: 50, min_column_width: 60, scales: [
            { unit: "year", step: 1, format: "%Y" },
          ]},
        ],
      });
      gantt.ext.zoom.setLevel("day");

      // Status icon helper
      function statusIcon(task: any) {
        const labels: Record<string, string> = {
          in_progress: "WIP", on_hold: "HOLD", at_risk: "RISK",
          delayed: "DELAY", done: "DONE", cancelled: "CANCEL",
        };
        const label = labels[task.status];
        if (!label) return "";
        const dark = darkModeRef.current;
        const colors: Record<string, string> = dark ? {
          in_progress: "#0D47A1", on_hold: "#BF360C", at_risk: "#8E1318", delayed: "#4A148C", done: "#1B5E20", cancelled: "#333"
        } : {
          in_progress: "#1976D2", on_hold: "#E65100", at_risk: "#C62828", delayed: "#6A1B9A", done: "#2E7D32", cancelled: "#555"
        };
        return `<span style="display:inline-block;margin-left:6px;padding:1px 5px;font-size:9px;font-weight:700;letter-spacing:0.5px;vertical-align:middle;color:#fff;border-radius:3px;line-height:1.4;background:${colors[task.status]}">${label}</span>`;
      }

      gantt.templates.rightside_text = function (_start: Date, _end: Date, task: any) {
        if (task.type === "milestone") return task.text + statusIcon(task);
        const barWidth = gantt.posFromDate(task.end_date) - gantt.posFromDate(task.start_date);
        const tagExtra = task.status ? 45 : 0;
        const textWidth = task.text.length * 7 + tagExtra;
        if (barWidth < textWidth + 20) return task.text + statusIcon(task);
        return "";
      };

      gantt.templates.task_text = function (_start: Date, _end: Date, task: any) {
        const barWidth = gantt.posFromDate(task.end_date) - gantt.posFromDate(task.start_date);
        const tagExtra = task.status ? 45 : 0;
        const textWidth = task.text.length * 7 + tagExtra;
        if (barWidth < textWidth + 20) return "";
        return task.text + statusIcon(task);
      };

      gantt.attachEvent("onContextMenu", (taskId: any, linkId: any, e: any) => {
        e.preventDefault();
        const x = e.clientX;
        const y = e.clientY;

        if (linkId) {
          if (!canEditRef.current) return false;
          const link = gantt.getLink(linkId);
          const sourceTask = gantt.getTask(link.source);
          const targetTask = gantt.getTask(link.target);
          setContextMenu({
            x, y,
            items: [{
              label: `Delete link: ${sourceTask.text} → ${targetTask.text}`,
              danger: true,
              onClick: () => gantt.deleteLink(linkId),
            }],
          });
        } else if (taskId) {
          const task = gantt.getTask(taskId);
          if (canEditRef.current) {
            setContextMenu({
              x, y,
              items: [
                { label: "Edit", onClick: () => gantt.showLightbox(taskId) },
                { label: "Add subtask", onClick: () => gantt.createTask({ start_date: task.start_date }, taskId) },
                {
                  label: "Delete", danger: true,
                  onClick: () => {
                    setConfirmDialog({
                      message: `Delete "${task.text}"?`,
                      onConfirm: () => { gantt.deleteTask(taskId); setConfirmDialog(null); },
                    });
                  },
                },
              ],
            });
          }
        } else {
          if (!canEditRef.current) return false;
          let clickDate: Date | null = null;
          try {
            const scroll = gantt.getScrollState();
            const timelineEl = document.querySelector(".gantt_task_bg") as HTMLElement;
            if (timelineEl) {
              const rect = timelineEl.getBoundingClientRect();
              const xPos = e.clientX - rect.left + scroll.x;
              clickDate = gantt.dateFromPos(xPos);
            }
          } catch {}

          setContextMenu({
            x, y,
            items: [{
              label: "Add item",
              onClick: () => {
                const newTask: any = clickDate ? { start_date: clickDate } : {};
                gantt.createTask(newTask, undefined);
              },
            }],
          });
        }
        return false;
      });

      // Expand date range when tasks are added/updated outside current view
      gantt.attachEvent("onAfterTaskUpdate", (id: any) => {
        if (gantt.config.fit_tasks) return;
        const task = gantt.getTask(id);
        const state = gantt.getState();
        let changed = false;
        if (task.end_date && state.max_date && task.end_date > state.max_date) {
          gantt.config.end_date = gantt.date.add(task.end_date, 1, "month");
          changed = true;
        }
        if (task.start_date && state.min_date && task.start_date < state.min_date) {
          gantt.config.start_date = gantt.date.add(task.start_date, -1, "month");
          changed = true;
        }
        if (changed) gantt.render();
      });

      gantt.attachEvent("onAfterTaskAdd", (id: any) => {
        if (gantt.config.fit_tasks) return;
        const task = gantt.getTask(id);
        const state = gantt.getState();
        let changed = false;
        if (task.end_date && state.max_date && task.end_date > state.max_date) {
          gantt.config.end_date = gantt.date.add(task.end_date, 1, "month");
          changed = true;
        }
        if (task.start_date && state.min_date && task.start_date < state.min_date) {
          gantt.config.start_date = gantt.date.add(task.start_date, -1, "month");
          changed = true;
        }
        if (changed) gantt.render();
        // Scroll to the new task
        gantt.showTask(id);
      });

      // Save after drag operations (resize, move, progress)
      gantt.attachEvent("onAfterTaskDrag", (id: any, mode: any) => {
        const planId = activePlanIdForSave.current;
        if (!planId) return;
        const task = gantt.getTask(id);
        planService.updateTask(planId, String(id), {
          start_date: serializeDate(task.start_date),
          end_date: serializeDate(task.end_date),
          duration: task.duration,
          progress: task.progress,
          parentId: task.parent && task.parent !== 0 ? String(task.parent) : null,
        } as any).catch(console.error);
      });

      // Apply dark mode if it was set before gantt was ready
      if (darkModeRef.current) {
        gantt.setSkin("dark");
      }

      gantt.render();

      // Show gantt after skin is applied (prevents white flash in dark mode)
      requestAnimationFrame(() => {
        const layout = document.querySelector(".gantt_layout");
        if (layout) layout.classList.add("gantt-ready");
        const preload = document.getElementById("gantt-preload-dark");
        if (preload) preload.remove();
      });
      clearInterval(interval);
    }, 50);
    return () => clearInterval(interval);
  }, []);

  // ============================================
  // Config
  // ============================================

  const config: GanttConfig = useMemo(() => {
    const isMobile = window.innerWidth <= 768;
    const textWidth = isMobile ? 150 : 350;
    let gridWidth = 44; // add button
    if (columnVisibility.text) gridWidth += textWidth;
    if (columnVisibility.assigned_to) gridWidth += isMobile ? 80 : 120;
    if (columnVisibility.start_date) gridWidth += isMobile ? 80 : 100;
    if (columnVisibility.duration) gridWidth += 50;
    if (columnVisibility.status) gridWidth += isMobile ? 70 : 110;
    const parsedStart = viewStart ? parseDate(viewStart) : undefined;
    const parsedEnd = viewEnd ? parseDate(viewEnd) : undefined;

    return {
      row_height: isMobile ? 40 : 50,
      grid_width: gridWidth,
      grid_resize: true,
      ...(parsedStart ? { start_date: parsedStart } : {}),
      ...(parsedEnd ? { end_date: parsedEnd } : {}),
      order_branch: canEdit ? "marker" : false,
      order_branch_free: canEdit,
      readonly: !canEdit,
      drag_move: canEdit,
      drag_resize: canEdit,
      drag_progress: canEdit,
      drag_links: canEdit,
      columns: [
        ...(columnVisibility.text ? [{ name: "text", tree: true, width: textWidth, min_width: textWidth, resize: true }] : []),
        ...(columnVisibility.assigned_to ? [{
          name: "assigned_to", label: "Assigned To", align: "center" as const, width: 120, min_width: 120, resize: true,
          template: (task: any) => task.assigned_to || "",
        }] : []),
        ...(columnVisibility.start_date ? [{
          name: "start_date", label: "Start", align: "center" as const, width: 100, min_width: 100, resize: true,
          template: (task: any) => {
            if (!task.start_date) return "";
            const d = task.start_date;
            return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
          },
        }] : []),
        ...(columnVisibility.duration ? [{
          name: "duration", label: "Days", align: "center" as const, width: 50, min_width: 50, resize: true,
        }] : []),
        ...(columnVisibility.status ? [{
          name: "status", label: "Status", align: "center" as const, width: 110, min_width: 110, resize: true,
          template: (task: any) => {
            const dark = darkModeRef.current;
            const darkColors: Record<string, string> = {
              in_progress: "#0D47A1", on_hold: "#BF360C", at_risk: "#8E1318", delayed: "#4A148C", done: "#1B5E20", cancelled: "#333"
            };
            const status = STATUS_OPTIONS.find(s => s.value === task.status);
            if (!status || !status.value) return "";
            const color = dark ? (darkColors[status.value] || status.color) : status.color;
            return `<span style="display:inline-block;padding:2px 8px;border-radius:3px;color:#fff;font-size:11px;font-weight:700;letter-spacing:0.5px;line-height:1.4;background:${color}">${status.label}</span>`;
          },
        }] : []),
        ...(canEdit ? [{ name: "add", width: 44 }] : []),
      ],
    };
  }, [columnVisibility, canEdit, viewStart, viewEnd]);

  function toggleColumn(col: keyof ColumnVisibility) {
    setColumnVisibility(prev => ({ ...prev, [col]: !prev[col] }));
  }

  // Custom lightbox with marker support
  const activePlanIdRef = useRef(activePlanId);
  activePlanIdRef.current = activePlanId;

  const customLightbox = useMemo(() => {
    return <TaskEditorWithMarkers markersState={markersState} setMarkersState={setMarkersState} ganttRef={ganttRef} activePlanIdRef={activePlanIdRef} />;
  }, [markersState]);

  // ============================================
  // Render
  // ============================================

  if (loading) {
    return (
      <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ color: "#888" }}>Loading plan...</div>
      </div>
    );
  }

  return (
    <div style={{ height: inTeams ? "100vh" : "100%", display: "flex", flexDirection: "column" }}>
      <div className="gantt-toolbar">
        <PlanPicker
          plans={plans}
          activePlanId={activePlanId}
          onSelectPlan={setActivePlanId}
          onCreatePlan={() => setShowCreatePrompt(true)}
          onRenamePlan={handleRenamePlan}
          onDeletePlan={handleDeletePlan}
          onJoinPlan={() => setShowShareDialog("join")}
        />
        {activePlanId && canEdit && (
          <button
            className="gantt-column-menu-btn gantt-hide-mobile"
            onClick={() => setShowPlanSettings(true)}
            title="Plan settings"
          >
            Settings
          </button>
        )}
        {activePlanId && !canEdit && (
          <span className="gantt-readonly-badge">Read-only</span>
        )}
        <div className="gantt-column-menu-wrapper gantt-hide-mobile">
          <button className="gantt-column-menu-btn" onClick={() => setMenuOpen(!menuOpen)}>
            Columns ▾
          </button>
          {menuOpen && (
            <div className="gantt-column-menu">
              <label><input type="checkbox" checked={columnVisibility.text} onChange={() => toggleColumn("text")} /> Task Name</label>
              <label><input type="checkbox" checked={columnVisibility.assigned_to} onChange={() => toggleColumn("assigned_to")} /> Assigned To</label>
              <label><input type="checkbox" checked={columnVisibility.start_date} onChange={() => toggleColumn("start_date")} /> Start Date</label>
              <label><input type="checkbox" checked={columnVisibility.duration} onChange={() => toggleColumn("duration")} /> Duration</label>
              <label><input type="checkbox" checked={columnVisibility.status} onChange={() => toggleColumn("status")} /> Status</label>
            </div>
          )}
        </div>
        <div className="gantt-zoom-controls">
          <div className="gantt-zoom-menu-wrapper">
            <button className="gantt-column-menu-btn" onClick={() => setZoomMenuOpen(!zoomMenuOpen)}>
              {{ day: "Days", week: "Weeks", month: "Months", year: "Years" }[zoomLevel]} ▾
            </button>
            {zoomMenuOpen && (
              <div className="gantt-zoom-menu">
                {[
                  { value: "day", label: "Days" }, { value: "week", label: "Weeks" },
                  { value: "month", label: "Months" }, { value: "year", label: "Years" },
                ].map((opt) => (
                  <button key={opt.value}
                    className={`gantt-zoom-menu-item ${zoomLevel === opt.value ? "active" : ""}`}
                    onClick={() => {
                      setZoomLevel(opt.value); setZoomMenuOpen(false);
                      const gantt = ganttRef.current?.instance;
                      if (gantt) { gantt.ext.zoom.setLevel(opt.value); }
                    }}
                  >{opt.label}</button>
                ))}
              </div>
            )}
          </div>
          <input
            type="date"
            className="gantt-date-picker"
            value={viewStart}
            min="2000-01-01"
            max="2100-12-31"
            onKeyDown={(e) => e.preventDefault()}
            onChange={(e) => setViewStart(e.target.value)}
            title="View start date"
          />
          <input
            type="date"
            className="gantt-date-picker"
            value={viewEnd}
            min="2000-01-01"
            max="2100-12-31"
            onKeyDown={(e) => e.preventDefault()}
            onChange={(e) => setViewEnd(e.target.value)}
            title="View end date"
          />
          {inTeams && (
            <button className="gantt-column-menu-btn gantt-hide-mobile"
              onClick={() => setDarkMode(!darkMode)}
              title={darkMode ? "Switch to light mode" : "Switch to dark mode"}
            >{darkMode ? "Light" : "Dark"}</button>
          )}
          <div className="gantt-zoom-menu-wrapper gantt-hide-mobile">
            <button className="gantt-column-menu-btn" onClick={() => setExportMenuOpen(!exportMenuOpen)}>
              Export ▾
            </button>
            {exportMenuOpen && (() => {
              const planName = (plans.find(p => p.id === activePlanId)?.title || "plan").replace(/[^a-zA-Z0-9-_ ]/g, "").replace(/\s+/g, "-");
              return <div className="gantt-zoom-menu">
                {[
                  { label: "PDF", action: () => {
                    const g = ganttRef.current?.instance; if (!g) return;
                    const skin = darkMode ? "dark" : "terrace";
                    const cols = [...g.config.columns];
                    g.config.columns = cols.filter((c: any) => c.name !== "add");
                    const opts: any = { skin, raw: true, footer: " ", name: planName };
                    if (inTeams) opts.callback = (res: any) => { if (res?.url) downloadFromUrl(res.url, `${planName}.pdf`); };
                    g.exportToPDF(opts);
                    setTimeout(() => { g.config.columns = cols; g.render(); }, 100);
                  }},
                  { label: "PNG", action: () => {
                    const g = ganttRef.current?.instance; if (!g) return;
                    const skin = darkMode ? "dark" : "terrace";
                    const cols = [...g.config.columns];
                    g.config.columns = cols.filter((c: any) => c.name !== "add");
                    const opts: any = { skin, raw: true, footer: " ", name: planName };
                    if (inTeams) opts.callback = (res: any) => { if (res?.url) downloadFromUrl(res.url, `${planName}.png`); };
                    g.exportToPNG(opts);
                    setTimeout(() => { g.config.columns = cols; g.render(); }, 100);
                  }},
                  { label: "Excel", action: () => {
                    const g = ganttRef.current?.instance; if (!g) return;
                    const cols = [...g.config.columns];
                    g.config.columns = cols.filter((c: any) => c.name !== "add");
                    const opts: any = { visual: "base-colors", name: planName };
                    if (inTeams) opts.callback = (res: any) => { if (res?.url) downloadFromUrl(res.url, `${planName}.xlsx`); };
                    g.exportToExcel(opts);
                    setTimeout(() => { g.config.columns = cols; g.render(); }, 100);
                  }},
                  { label: "MS Project", action: () => {
                    const opts: any = { name: planName };
                    if (inTeams) opts.callback = (res: any) => { if (res?.url) downloadFromUrl(res.url, `${planName}.xml`); };
                    ganttRef.current?.instance?.exportToMSProject(opts);
                  }},
                  { label: "Primavera P6", action: () => {
                    const opts: any = { name: planName };
                    if (inTeams) opts.callback = (res: any) => { if (res?.url) downloadFromUrl(res.url, `${planName}-p6.xml`); };
                    ganttRef.current?.instance?.exportToPrimaveraP6(opts);
                  }},
                  { label: "iCal", action: () => {
                    const opts: any = { name: planName };
                    if (inTeams) opts.callback = (res: any) => { if (res?.url) downloadFromUrl(res.url, `${planName}.ics`); };
                    ganttRef.current?.instance?.exportToICal(opts);
                  }},
                  { label: "JSON", action: () => {
                    const g = ganttRef.current?.instance; if (!g) return;
                    const d = g.serialize();
                    const blob = new Blob([JSON.stringify(d, null, 2)], { type: "application/json" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a"); a.href = url; a.download = `${planName}.json`; a.click();
                    URL.revokeObjectURL(url);
                  }},
                ].map((item) => (
                  <button key={item.label} className="gantt-zoom-menu-item"
                    onClick={() => { item.action(); setExportMenuOpen(false); }}
                  >{item.label}</button>
                ))}
              </div>;
            })()}
          </div>
        </div>
      </div>

      {activePlanId ? (
        <ReactGantt
          tasks={planData.tasks}
          links={planData.links}
          markers={markers}
          config={config}
          templates={ganttTemplates}
          data={dataCallback}
          ref={ganttRef}
          customLightbox={canEdit ? customLightbox : undefined}
          plugins={{ export_api: true }}
        />
      ) : (
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 16 }}>
          <p style={{ fontSize: 16, color: "#888" }}>No plans yet. Create one to get started.</p>
          <button
            className="gantt-column-menu-btn"
            style={{ padding: "10px 20px", fontSize: 14 }}
            onClick={() => setShowCreatePrompt(true)}
          >
            + Create Plan
          </button>
        </div>
      )}

      {contextMenu && (
        <ContextMenu x={contextMenu.x} y={contextMenu.y} items={contextMenu.items} onClose={closeContextMenu} />
      )}
      {editingMarker && (
        <TaskEditor
          data={{ text: editingMarker.text, type: "marker", color: editingMarker.color, start_date: editingMarker.start_date }}
          onSave={(updated: any) => {
            if (updated.type === "marker") {
              setMarkersState(prev => prev.map(m =>
                m.id === editingMarker.id ? { ...m, text: updated.text, start_date: updated.start_date, color: updated.color } : m
              ));
              if (activePlanId) {
                planService.updateMarker(activePlanId, editingMarker.id, {
                  text: updated.text, start_date: serializeDate(updated.start_date), color: updated.color,
                } as any).catch(console.error);
              }
            } else {
              setMarkersState(prev => prev.filter(m => m.id !== editingMarker.id));
              if (activePlanId) {
                planService.deleteMarker(activePlanId, editingMarker.id).catch(console.error);
              }
              const gantt = ganttRef.current?.instance;
              if (gantt) {
                gantt.addTask({
                  text: updated.text, type: updated.type, color: updated.color,
                  start_date: updated.start_date,
                  duration: updated.type === "milestone" ? 0 : (updated.duration || 1),
                  progress: updated.progress || 0, assigned_to: updated.assigned_to || "",
                  ...(updated.end_date && updated.type !== "milestone" ? { end_date: updated.end_date } : {}),
                });
              }
            }
            setEditingMarker(null);
          }}
          onCancel={() => setEditingMarker(null)}
          onDelete={() => {
            setMarkersState(prev => prev.filter(m => m.id !== editingMarker.id));
            if (activePlanId) {
              planService.deleteMarker(activePlanId, editingMarker.id).catch(console.error);
            }
            setEditingMarker(null);
          }}
        />
      )}
      {confirmDialog && (
        <ConfirmDialog message={confirmDialog.message} onConfirm={confirmDialog.onConfirm} onCancel={() => setConfirmDialog(null)} />
      )}
      {showRating && (
        <RatingDialog
          onSubmit={(rating, feedback) => {
            // Mark as permanently done so dismiss handler doesn't also fire
            localStorage.setItem("gantt-rating-dismiss-count", "99");
            const token = localStorage.getItem("gantt-auth-token") || "";
            fetch(`${process.env.REACT_APP_FUNC_ENDPOINT || ""}/api/rating/submit`, {
              method: "POST",
              headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
              body: JSON.stringify({ rating, feedback }),
            }).catch(() => {});
          }}
          onDismiss={() => {
            setShowRating(false);
            const count = parseInt(localStorage.getItem("gantt-rating-dismiss-count") || "0") + 1;
            localStorage.setItem("gantt-rating-dismiss-count", String(count));
            localStorage.setItem("gantt-rating-last-dismiss", String(Date.now()));
            if (count >= 2) {
              const token = localStorage.getItem("gantt-auth-token") || "";
              fetch(`${process.env.REACT_APP_FUNC_ENDPOINT || ""}/api/rating/submit`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify({ rating: 0, feedback: "Dismissed twice" }),
              }).catch(() => {});
            }
          }}
          onClose={() => setShowRating(false)}
        />
      )}
      {showCreatePrompt && (
        <PromptDialog
          title="New Plan"
          label="Plan name"
          placeholder="My Project Plan"
          onConfirm={(title) => { handleCreatePlan(title); setShowCreatePrompt(false); }}
          onCancel={() => setShowCreatePrompt(false)}
        />
      )}
      {showShareDialog === "join" && (
        <ShareDialog
          planId={activePlanId || ""}
          planTitle=""
          contextId={contextId}
          mode="join"
          onJoined={async (joinedPlanId) => {
            try {
              const loadedPlans = await planService.listPlans(contextId);
              setPlans(loadedPlans);
              setActivePlanId(joinedPlanId);
            } catch {}
          }}
          onCancel={() => setShowShareDialog(null)}
        />
      )}
      {showPlanSettings && activePlanId && (
        <PlanSettings
          plan={plans.find(p => p.id === activePlanId)!}
          onSave={async (updates) => {
            try {
              await planService.updatePlan(activePlanId, updates as any);
              setPlans(prev => prev.map(p => p.id === activePlanId
                ? { ...p, title: updates.title, editMode: updates.editMode as any, editors: updates.editors }
                : p));
            } catch (err) {
              console.error("Failed to update plan settings:", err);
            }
            setShowPlanSettings(false);
          }}
          onDelete={() => {
            setShowPlanSettings(false);
            handleDeletePlan(activePlanId);
          }}
          onCancel={() => setShowPlanSettings(false)}
        />
      )}
    </div>
  );
}

// Wrapper that intercepts marker saves
function TaskEditorWithMarkers({ markersState, setMarkersState, ganttRef, activePlanIdRef, ...injectedProps }: any) {
  const { data, onSave, onCancel, onDelete } = injectedProps;

  let hasChildren = false;
  const gantt = ganttRef.current?.instance;
  if (gantt && data?.id && !data?.$new && gantt.isTaskExists(data.id)) {
    hasChildren = gantt.hasChild(data.id);
  }

  function handleSave(updated: any) {
    if (updated.type === "marker") {
      const planId = activePlanIdRef.current;
      if (planId) {
        planService.createMarker(planId, {
          text: updated.text,
          start_date: serializeDate(updated.start_date),
          color: updated.color || "#F44336",
        } as any).then((created) => {
          setMarkersState((prev: MarkerData[]) => [
            ...prev,
            { id: created.id, text: created.text, start_date: new Date(created.start_date), color: created.color },
          ]);
        }).catch(console.error);
      }
      if (data?.$new) { onCancel?.(); } else { onDelete?.(); }
    } else {
      onSave?.(updated);
    }
  }

  return (
    <TaskEditor data={data} onSave={handleSave} onCancel={onCancel} onDelete={onDelete} hasChildren={hasChildren} planId={activePlanIdRef.current} />
  );
}

export default Tab;
